import type { QuerySpec } from '@helios/powersync-client-core';

/* ------------------------------------------------------------------ */
/*  Symptoms & Diagnosis (PRD.214)                                    */
/*  One row per chip; suggestions derived from past usage.            */
/* ------------------------------------------------------------------ */

// ─── Row Types ───

export interface PatientSymptomEntryRow {
  id: string;
  org_id: string;
  clinic_id: string | null;
  patient_id: string;
  encounter_id: string;
  label: string;
  /** PRD.217 — null = clinician-typed; 'ai' = AI-suggested (badge shown). */
  source: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface PatientDiagnosisEntryRow {
  id: string;
  org_id: string;
  clinic_id: string | null;
  patient_id: string;
  encounter_id: string;
  label: string;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface PatientTreatmentEntryRow {
  id: string;
  org_id: string;
  clinic_id: string | null;
  patient_id: string;
  encounter_id: string;
  label: string;
  /** PRD.217 — null = clinician-typed; 'ai' = AI-suggested (badge shown). */
  source: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface VttSuggestionRow {
  label: string;
}

// ─── Queries ───

/** Symptom chips for an encounter, ordered by creation. */
export function qSymptomsByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient_symptom_entries', 'by-encounter', encounterId] as const,
    sql: `SELECT * FROM patient_symptom_entries
          WHERE encounter_id = ?
          ORDER BY created_at ASC`,
    params: [encounterId],
    watch: ['patient_symptom_entries'],
  };
}

/** Diagnosis chips for an encounter, ordered by creation. */
export function qDiagnosesByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient_diagnosis_entries', 'by-encounter', encounterId] as const,
    sql: `SELECT * FROM patient_diagnosis_entries
          WHERE encounter_id = ?
          ORDER BY created_at ASC`,
    params: [encounterId],
    watch: ['patient_diagnosis_entries'],
  };
}

/**
 * Suggestion catalog for Symptoms — derived from past usage in this org.
 * PRD §8.1: not a rigid master dictionary; the catalog grows from real entries.
 */
export function qSymptomSuggestions(
  orgId: string,
  search: string,
  limit = 20,
): QuerySpec<[string, string, number]> {
  return {
    key: ['patient_symptom_entries', 'suggestions', orgId, search] as const,
    sql: `SELECT DISTINCT label
          FROM patient_symptom_entries
          WHERE org_id = ?
            AND LOWER(label) LIKE LOWER(?)
          ORDER BY label ASC
          LIMIT ?`,
    params: [orgId, `%${search}%`, limit],
    watch: ['patient_symptom_entries'],
  };
}

/** Suggestion catalog for Diagnosis — derived from past usage in this org. */
export function qDiagnosisSuggestions(
  orgId: string,
  search: string,
  limit = 20,
): QuerySpec<[string, string, number]> {
  return {
    key: ['patient_diagnosis_entries', 'suggestions', orgId, search] as const,
    sql: `SELECT DISTINCT label
          FROM patient_diagnosis_entries
          WHERE org_id = ?
            AND LOWER(label) LIKE LOWER(?)
          ORDER BY label ASC
          LIMIT ?`,
    params: [orgId, `%${search}%`, limit],
    watch: ['patient_diagnosis_entries'],
  };
}

/** Treatment chips for an encounter, ordered by creation (PRD.217). */
export function qTreatmentsByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient_treatment_entries', 'by-encounter', encounterId] as const,
    sql: `SELECT * FROM patient_treatment_entries
          WHERE encounter_id = ?
          ORDER BY created_at ASC`,
    params: [encounterId],
    watch: ['patient_treatment_entries'],
  };
}

/** Suggestion catalog for Treatments — derived from past usage in this org. */
export function qTreatmentSuggestions(
  orgId: string,
  search: string,
  limit = 20,
): QuerySpec<[string, string, number]> {
  return {
    key: ['patient_treatment_entries', 'suggestions', orgId, search] as const,
    sql: `SELECT DISTINCT label
          FROM patient_treatment_entries
          WHERE org_id = ?
            AND LOWER(label) LIKE LOWER(?)
          ORDER BY label ASC
          LIMIT ?`,
    params: [orgId, `%${search}%`, limit],
    watch: ['patient_treatment_entries'],
  };
}
